import { omit } from 'lodash';
import { Dispatch } from 'react';
import {
  useQueries,
  UseQueryResult,
  useQuery,
  UseQueryOptions,
  // useQueryClient,
} from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { request } from '../../../common/src/legacy/services/httpSessionService';
import {
  // isFailedQuote,
  LouiseQuote,
  ProductsQualityRespWithPagination,
  ProductWithQuality,
} from '../../../../types/Quotes';
import useQueryFetch from '../../../common/src/hooks/useQueryFetch';
import useSessionId from '../../../flows/src/hooks/useSessionId';
import { RootState } from '../store/redux/reducers/rootReducer';
import {
  getTopProductsBySessionId,
  TopProductOptions,
} from '../services/louiseApiService';
import {
  QuoteSpecifications,
  addQuoteIdsToUrl,
  addProductKeysToId,
} from '../store/redux/slices/offersSlice';
import { getProductKey } from '../services/offersService';

// Quote prices stay 30 minutes fresh
export const OFFERS_CACHE_TIME = 1000 * 60 * 30;

export const getFlatProductsResponse = (
  data?: ProductsQualityRespWithPagination,
): ProductWithQuality[] =>
  Object.values(
    omit(data, [
      'pagination',
      'risk_object_errors',
    ]) as ProductsQualityRespWithPagination,
  )
    .filter((item) => !!item)
    .flat();

const getQuoteSpecs = (
  quoteSpecs?: QuoteSpecifications,
): QuoteSpecifications => {
  if (quoteSpecs) {
    return {
      ...quoteSpecs,
    };
  }
  return {
    save_quote: true,
  };
};

export const onProductSuccess = (
  data: ProductsQualityRespWithPagination,
  dispatch: Dispatch<unknown>,
): void => {
  // Set HASH map
  dispatch(
    addQuoteIdsToUrl(
      Object.assign(
        {},
        ...getFlatProductsResponse(data).map(({ hash_id, self }) => ({
          [hash_id]: self.replace('/quotes/v1', '/quotes/v2'),
        })),
      ),
    ),
  );
  dispatch(
    addProductKeysToId(
      Object.assign(
        {},
        ...getFlatProductsResponse(data).map((offer) => ({
          [offer.hash_id]: getProductKey(offer),
        })),
      ),
    ),
  );
};

/**
 * Gets a list of the top X products for a session
 */
export const useTopProducts = (
  riskObject: string,
  params?: TopProductOptions,
): UseQueryResult<ProductsQualityRespWithPagination, unknown> => {
  const sessionId = useSessionId();
  const dispatch = useDispatch();

  return useQueryFetch<ProductsQualityRespWithPagination>({
    queryKey: [sessionId, 'products', riskObject],
    fetchFn: () => getTopProductsBySessionId(sessionId, riskObject, params),
    successCb: (data: ProductsQualityRespWithPagination) =>
      onProductSuccess(data, dispatch),
    options: {
      staleTime: Infinity,
      cacheTime: OFFERS_CACHE_TIME,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      retry: false,
    },
  });
};

// /**
//  * Gets a single product out of the query cache
//  */
export const useTopProduct = (
  riskObject: string,
  productHashId?: string,
  params?: TopProductOptions,
): UseQueryResult<ProductWithQuality | undefined, unknown> => {
  const sessionId = useSessionId();
  const result = useQueryFetch<ProductsQualityRespWithPagination>({
    queryKey: [sessionId, 'products', riskObject],
    fetchFn: () => getTopProductsBySessionId(sessionId, riskObject, params),
    options: {
      staleTime: Infinity,
      cacheTime: OFFERS_CACHE_TIME,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      // This fixes a bug where fetchFn would take an older value of riskObject (when it was undefined)
      // enabled: Boolean(sessionId && riskObjects?.length > 0),
    },
  });

  const products = getFlatProductsResponse(result.data);
  return {
    ...result,
    data:
      result.data && products.find(({ hash_id }) => hash_id === productHashId),
  } as UseQueryResult<ProductWithQuality, unknown>;
};

/**
 * Fetches a single quote
 */
export const useQuote = (
  riskObject: string,
  quoteHashId?: string,
  carFilter?: string,
): UseQueryResult<LouiseQuote> => {
  const productKeysToId = useSelector(
    (state: RootState) => state.offers.productKeysToId,
  );
  const quoteSpecs = useSelector((state: RootState) => state.offers.quoteSpecs);
  const quoteSpecsByHash =
    quoteSpecs?.[productKeysToId[quoteHashId || ''] || ''];
  const sessionId = useSessionId();
  const quoteIdToUrl = useSelector(
    (state: RootState) => state.offers.quoteIdToUrl,
  );
  const chosenRiskPbjects = useSelector(
    (state: RootState) => state.offers.chosenRiskObjectsForTarif,
  );
  const path = quoteIdToUrl[quoteHashId || ''];

  const queryKey = [
    sessionId,
    'quotes',
    riskObject,
    carFilter,
    quoteHashId,
    quoteSpecsByHash,
  ];
  // const queryClient = useQueryClient();
  // const quoteCacheData: LouiseQuote | undefined = queryClient.getQueryData(
  //   queryKey,
  // );

  // Invalidate a query if it failed
  // if (quoteCacheData && isFailedQuote(quoteCacheData)) {
  //   queryClient.removeQueries(queryKey);
  // }

  return useQuery<LouiseQuote>({
    queryKey,
    queryFn: async () => {
      if (path) {
        const [result] = await request({
          path: path,
          method: 'POST',
          payload: {
            id: sessionId,
            quote_specifications: getQuoteSpecs(quoteSpecsByHash),
            ...(chosenRiskPbjects[riskObject] && {
              [`${riskObject}_revision_id`]: chosenRiskPbjects[riskObject],
            }),
          },
        });
        return result;
      }
    },
    // staleTime: Infinity,
    // cacheTime: OFFERS_CACHE_TIME,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    enabled: Boolean(quoteHashId && riskObject),
  });
};

/**
 * Fetches a list of quotes
 */
export const useQuotes = (quotes?: {
  [riskObject: string]: string[] | string;
}): { data: LouiseQuote[]; isFetched: boolean } => {
  const sessionId = useSessionId();
  const quoteIdToUrl = useSelector(
    (state: RootState) => state.offers.quoteIdToUrl,
  );
  const productKeysToId = useSelector(
    (state: RootState) => state.offers.productKeysToId,
  );
  const quoteSpecs = useSelector((state: RootState) => state.offers.quoteSpecs);
  const entries = Object.entries(quotes || {})
    .map(([riskObject, quoteIds]) =>
      (Array.isArray(quoteIds) ? quoteIds : [quoteIds]).map((quoteId) => ({
        queryKey: [
          sessionId,
          'quotes',
          riskObject,
          quoteId,
          quoteSpecs[productKeysToId[quoteId || ''] || ''],
        ],
        queryFn: async () => {
          const result = (
            await request({
              path: quoteIdToUrl[quoteId],
              method: 'POST',
              payload: {
                id: sessionId,
                quote_specifications: getQuoteSpecs(
                  quoteSpecs[productKeysToId[quoteId || ''] || ''],
                ),
              },
            })
          )[0] as LouiseQuote;
          return result;
        },
        staleTime: Infinity,
        cacheTime: OFFERS_CACHE_TIME,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
      })),
    )
    .flat();
  const data = useQueries(entries as UseQueryOptions[]);

  const isFetched = data.every(({ isFetched }) => isFetched);

  const state = data
    .filter(({ data }) => !!data)
    .map(({ data }) => data) as LouiseQuote[];

  return { data: state, isFetched };
};
